/**
 * Create the Renewal Vehicle Agent node
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail, reportDataRequiredGuardrail } from '../guardrails';
import { searchFAQs, formatSearchResults } from '../../utils/faq-search';

export function createRenewalVehicleAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  return {
    id: 'renewal_vehicle_agent',
    name: 'Renewal Vehicle Agent',
    description: 'Step 2: Vehicle selection for renewal',
    
    // Initial prompt to trigger AI behavior

    
    // Backend provides the actual system instruction via ephemeral token

    
    // Initial prompt to trigger the AI to present vehicle selection
    initialPrompt: 'The report data has been found. Tell the user to select their vehicle from the list shown on screen. Do NOT read the vehicle details aloud - just guide them to tap/click on their vehicle.',
    
    // Must have report data
    entryGuardrails: {
      required: [languageDetectedGuardrail, reportDataRequiredGuardrail],
      optional: [],
    },
    
    exitGuardrails: {
      transitions: new Map([
        ['vehicle_selected', {
          target: { agentId: 'renewal_confirm_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['cancel', {
          target: { agentId: 'router_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
      ]),
      fallback: { agentId: 'router_agent' },
    },
    
    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {
      if (toolName === 'vehicle_selected') {
        const plateNumber = args.plate_number;
        const plateSource = args.plate_source;
        
        console.log('[RenewalVehicleAgent] Vehicle selected:', plateNumber, plateSource);
        
        return {
          toolName,
          args,
          result: { 
            success: true, 
            message: `Selected vehicle: ${plateNumber}`,
          },
          triggersTransition: true,
          transitionTo: 'renewal_confirm_agent',
          transitionData: {
            selectedVehicle: {
              Plate: {
                Number: plateNumber,
                Source: { Description: plateSource },
              },
            },
          },
        };
      }
      
      if (toolName === 'cancel_renewal') {
        console.log('[RenewalVehicleAgent] User cancelled');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Cancelled' },
          triggersTransition: true,
          transitionTo: 'router_agent',
          transitionData: {
            reportData: undefined,
            selectedVehicle: undefined,
          },
        };
      }
      
      // Inline FAQ - answer questions without leaving renewal flow
      if (toolName === 'search_faq') {
        const question = args.question || '';
        const searchResults = await searchFAQs(question, 3, 2);
        const language = context.transitionData.language || 'en';
        const formattedResults = formatSearchResults(searchResults, language);
        
        console.log('[RenewalVehicleAgent] Inline FAQ:', question);
        
        return {
          toolName,
          args,
          result: {
            success: true,
            results_count: searchResults.length,
            results: formattedResults,
            message: 'Answer the question, then remind user to select their vehicle.',
          },
          triggersTransition: false,
        };
      }
      
      // Universal navigation: Switch to lost report search
      if (toolName === 'start_lost_report_search') {
        console.log('[RenewalVehicleAgent] Switching to lost report search');
        return {
          toolName,
          args,
          result: { success: true, message: 'Switching to lost report search' },
          triggersTransition: true,
          transitionTo: 'lost_report_upload_extract_agent',
        };
      }
      
      // Universal navigation: Switch to accident reporting
      if (toolName === 'start_accident_reporting') {
        console.log('[RenewalVehicleAgent] Switching to accident reporting');
        return {
          toolName,
          args,
          result: { success: true, message: 'Switching to accident reporting' },
          triggersTransition: true,
          transitionTo: 'accident_start_agent',
        };
      }
      
      // Universal navigation: Switch to FAQ
      if (toolName === 'go_to_faq') {
        console.log('[RenewalVehicleAgent] Switching to FAQ');
        return {
          toolName,
          args,
          result: { success: true, message: 'Switching to FAQ' },
          triggersTransition: true,
          transitionTo: 'faq_agent',
        };
      }
      
      return {
        toolName,
        args,
        result: { success: false, error: 'Unknown tool' },
      };
    },
    
    onActivate: async (context: AgentContext) => {
      const vehicleCount = context.transitionData.reportData?.Vehicles?.length || 0;
      console.log('[RenewalVehicleAgent] Activated - showing', vehicleCount, 'vehicles');
    },
    
    onDeactivate: async (context: AgentContext) => {
      console.log('[RenewalVehicleAgent] Deactivated');
    },
  };
}

export default createRenewalVehicleAgent;

